import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { deleteWithUndo } from '../lib/undoDelete'
import { fetchRows } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { SEED_TEMPLATES, HOURS, checkSchedule, feasibility, rebalanceOptions, normaliseMin } from '../lib/mlcRoster'
import { todayLocal } from '../lib/dates'
import { shipTodayAuto } from '../lib/shipTime'
import html2pdf from 'html2pdf.js'
import DateField from '../components/DateField'
import { escHtml } from '../lib/escHtml'

export default function WatchSchedulePage({ onNavigate }) {
  const { config } = useAuth()
  return (
    <div style={{ padding: 20, maxWidth: 1400, margin: '0 auto' }}>
      <WatchSchedule config={config} onNavigate={onNavigate} />
    </div>
  )
}

function WatchSchedule({ config, onNavigate }) {
  const [crew, setCrew] = useState([])
  useEffect(() => {
    if (!config?.id) return
    ;(async () => {
      try {
        const rows = await fetchRows('crew')
        const gv = v => (!v ? '' : (typeof v === 'object' && v.value) ? v.value : String(v))
        const list = (rows || [])
          .filter(r => r['Onboard'] === true || r['Onboard'] === 'true')
          .map(r => ({
            id: r.id,
            name: (gv(r['First Name']) + ' ' + gv(r['Last Name'])).trim(),
            role: gv(r['Position']) || gv(r['Role']) || '',
          })).filter(c => c.name)
        setCrew(list)
      } catch (e) { /* crew list optional */ }
    })()
  }, [config?.id])
  const [tplId, setTplId] = useState(SEED_TEMPLATES[0].id)
  const [rows, setRows] = useState(() => SEED_TEMPLATES[0].roles.map(r => ({ ...r })))
  const [minOnDeck, setMinOnDeck] = useState(() => normaliseMin(SEED_TEMPLATES[0].minOnDeck, SEED_TEMPLATES[0].coverageWindow))
  const [covWin, setCovWin] = useState(SEED_TEMPLATES[0].coverageWindow || null)
  const [painting, setPainting] = useState(null)
  const [saving, setSaving] = useState(false)
  const [dateFrom, setDateFrom] = useState(shipTodayAuto())
  const [dateTo, setDateTo] = useState(shipTodayAuto())
  const [archive, setArchive] = useState([])
  const [showArchive, setShowArchive] = useState(false)
  const [proposal, setProposal] = useState(null)
  const [aiText, setAiText] = useState('')
  const [aiBusy, setAiBusy] = useState(false)
  const [aiProposal, setAiProposal] = useState(null)

  // Handoff from the AI Assistant. The brain passes the request here; this page owns
  // the only builder (checkSchedule + rebalanceOptions, both verified).
  const [autoFired, setAutoFired] = useState(false)
  useEffect(() => {
    if (autoFired) return
    let req = ''
    try { req = localStorage.getItem('hc_watch_request') || '' } catch {}
    if (!req) return
    try { localStorage.removeItem('hc_watch_request') } catch {}
    setAutoFired(true)
    setAiText(req)
  }, [autoFired])

  useEffect(() => {
    if (!autoFired || !aiText) return
    let pending = false
    try { pending = !!localStorage.getItem('hc_watch_pending') } catch {}
    if (!pending) return
    try { localStorage.removeItem('hc_watch_pending') } catch {}
    askAi()
  }, [autoFired, aiText]) // eslint-disable-line

  async function askAi() {
    if (!aiText.trim()) return
    setAiBusy(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const resp = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (session?.access_token || '') },
        body: JSON.stringify({ action: 'modify_roster', text: aiText, rows, minOnDeck }),
      })
      const out = await resp.json()
      // The model names the WORKING HOURS; code builds the 24-char row. Models cannot
      // reliably count to 24 in a string (it returned 26 chars), so the string is never
      // asked for. Built here, a wrong length is structurally impossible.
      const cellsFromHours = hrs => {
        const on = new Set((hrs || []).map(Number).filter(h => Number.isInteger(h) && h >= 0 && h <= 23))
        return Array.from({ length: 24 }, (_, h) => (on.has(h) ? 'W' : 'R')).join('')
      }
      const raw = out.changes || []
      const changes = raw
        .filter(c => typeof c.row === 'number' && rows[c.row] && Array.isArray(c.work) && c.work.length)
        .map(c => ({ row: c.row, cells: cellsFromHours(c.work) }))
      if (!changes.length) {
        const why = raw.length
          ? 'The AI returned ' + raw.length + ' change(s) but none named valid working hours (0-23).'
          : (out.reasoning || 'Could not make that change.') + (out.raw ? '\n\nModel said:\n' + out.raw : '')
        alert(why)
        setAiBusy(false); return
      }

      const next = rows.map((r, i) => {
        const c = changes.find(x => x.row === i)
        return c ? { ...r, cells: c.cells } : r
      })
      const after = checkSchedule(next, minOnDeck, covWin)
      // The model narrates hour totals and gets them wrong (claimed 12h when the grid
      // says 13h). The row counts below are computed from the cells and are the truth,
      // so strip any hour claim from the prose rather than let it contradict them.
      const cleanReason = String(out.reasoning || '')
        .replace(/,?\s*(while |and |whilst )?(maintaining|keeping|preserving|retaining)[^.,;]*\b\d+\s*(work |working )?hours?\b/gi, '')
        .replace(/\s{2,}/g, ' ')
        .trim()
      setAiProposal({ rows: next, changes, reasoning: cleanReason, after })
    } catch (e) { alert('AI failed: ' + e.message) }
    setAiBusy(false)
  }
  const [compare, setCompare] = useState(null)
  const [comparing, setComparing] = useState(false)

  async function runCompare() {
    const assigned = rows.filter(r => r.crew_name)
    if (!assigned.length) { alert('Assign crew to the roster rows first - the comparison needs real people.'); return }
    setComparing(true)
    try {
      const all = (await fetchRows('hours_rest')) || []
      const gv = v => (!v ? '' : (typeof v === 'object' && v.value) ? v.value : String(v))

      // every date in the range
      const dates = []
      for (let d = new Date(dateFrom + 'T00:00:00'); d <= new Date(dateTo + 'T00:00:00'); d.setDate(d.getDate() + 1)) {
        dates.push(todayLocal(new Date(d)))
      }

      const result = assigned.map(r => {
        const planned = r.cells.split('')
        const plannedWork = planned.filter(c => c === 'W').length
        // one hours-of-rest record per crew member per month: merge all their months
        let days = {}
        all.filter(x => gv(x['Crew Member']).trim().toLowerCase() === r.crew_name.trim().toLowerCase())
          .forEach(rec => { try { Object.assign(days, JSON.parse(gv(rec['Days']) || '{}')) } catch (e) {} })

        const perDay = dates.map(date => {
          const d = days[date]
          if (!d || !Array.isArray(d.blocks)) return { date, recorded: null }
          // 48 half-hour blocks -> 24 hours; an hour counts as worked if either block is work
          const actual = []
          for (let h = 0; h < 24; h++) actual.push((d.blocks[h * 2] || d.blocks[h * 2 + 1]) ? 'W' : 'R')
          const actualWork = actual.filter(c => c === 'W').length
          const diffHours = actual.map((v, h) => v !== planned[h] ? h : -1).filter(h => h >= 0)
          return { date, recorded: actual, actualWork, delta: actualWork - plannedWork, diffHours }
        })

        const withData = perDay.filter(d => d.recorded)
        const totalDelta = withData.reduce((n, d) => n + d.delta, 0)
        const identical = withData.length > 0 && withData.every(d => d.diffHours.length === 0)
        return { who: r.crew_name, role: r.role, plannedWork, perDay, withData: withData.length,
                 totalDelta, identical, missing: perDay.length - withData.length }
      })

      setCompare({ dates, result })
    } catch (e) { alert('Compare failed: ' + e.message) }
    setComparing(false)
  }

  async function loadArchive() {
    const { data } = await supabase.from('watch_schedules').select('*')
      .eq('client_id', config.id).order('created_at', { ascending: false }).limit(50)
    setArchive(data || [])
  }
  useEffect(() => { if (config?.id) loadArchive() }, [config?.id])

  const days = Math.max(1, Math.round((new Date(dateTo + 'T00:00:00') - new Date(dateFrom + 'T00:00:00')) / 86400000) + 1)

  const tpl = SEED_TEMPLATES.find(t => t.id === tplId)
  const res = checkSchedule(rows, minOnDeck, covWin)
  const feas = feasibility(rows.length, minOnDeck, covWin)

  function loadTemplate(id) {
    const t = SEED_TEMPLATES.find(x => x.id === id)
    if (!t) return
    setTplId(id)
    setRows(t.roles.map(r => ({ ...r })))
    setMinOnDeck(normaliseMin(t.minOnDeck, t.coverageWindow || null))
    setCovWin(t.coverageWindow || null)
  }

  function setCell(ri, h, val) {
    setRows(rs => rs.map((r, i) => {
      if (i !== ri) return r
      const arr = r.cells.split('')
      arr[h] = val
      return { ...r, cells: arr.join('') }
    }))
  }

  function addCrew() {
    setRows(rs => [...rs, { role: 'CREW ' + (rs.length + 1), cells: 'R'.repeat(24) }])
  }
  function removeCrew(i) {
    setRows(rs => rs.filter((_, j) => j !== i))
  }
  function applyVariant(cells) {
    setRows(rs => rs.map(r => r.role === 'CAPTAIN' ? { ...r, cells } : r))
  }

  async function save() {
    const name = prompt('Schedule name', tpl ? tpl.name : 'Watch schedule')
    if (!name) return
    setSaving(true)
    const { error } = await supabase.from('watch_schedules').insert({
      client_id: config.id, name, scenario: tpl?.scenario || 'custom',
      min_on_deck: Math.max(0, ...minOnDeck), min_on_deck_hours: minOnDeck, coverage_window: covWin, grid: rows,
      date_from: dateFrom, date_to: dateTo,
    })
    if (error) alert('Save failed: ' + error.message)
    else { await loadArchive(); alert('Saved.') }
    setSaving(false)
  }

  function runRebalance() {
    const fix = rebalanceOptions(rows, minOnDeck, covWin)
    if (!fix.options.length && !fix.note) { alert('Nothing to fix - coverage is already met.'); return }
    setProposal(fix)
  }
  function acceptOption(o) {
    setRows(o.rows)
    setProposal(null)
  }

  function fmtLong(iso) {
    if (!iso) return ''
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  function exportPdf() {
    const vessel = (config && config.vessel_name) || 'Vessel'
    const uniq = [...new Set(minOnDeck)]
    const covDesc = uniq.length === 1
      ? (uniq[0] > 0 ? 'min ' + uniq[0] + ' on deck' : 'no coverage requirement')
      : 'coverage: ' + minOnDeck.map((n, h) => n > 0 ? String(h).padStart(2, '0') + ':00\u2013' + String((h + 1) % 24).padStart(2, '0') + ':00 \u2265' + n : null)
          .filter(Boolean).length + ' hours with a minimum set'
    const NAVY = '#0A1628', GOLD = '#C9A84C', LINE = '#E5E9F1', MUTE = '#67748C', TEXT = '#16233B', BLUE = '#1155F5'
    const wave = '<span style="display:inline-block;font-size:30px;font-weight:900;color:' + BLUE + ';line-height:1;letter-spacing:-3px;">\u2248</span>'

    const hdr = HOURS.map(h => '<th style="padding:5px 2px;font-size:8px;color:#fff;background:' + NAVY + ';">' + h.slice(0, 2) + '</th>').join('')
    const body = res.perRow.map(r => {
      const c = r.check
      const bad = c.issues.some(i => i.level === 'red')
      const cells = r.cells.split('').map(v =>
        '<td style="padding:4px 2px;text-align:center;font-size:7.5px;font-weight:700;border:1px solid #fff;background:' +
        (v === 'W' ? 'rgba(23,138,92,.16);color:#0f6a47' : 'rgba(201,59,71,.10);color:#a33') + ';">' +
        (v === 'W' ? 'Work' : 'Rest') + '</td>').join('')
      return '<tr><td style="padding:5px 8px;font-weight:700;font-size:9px;white-space:nowrap;border-left:3px solid ' +
        (bad ? '#C93B47' : 'transparent') + ';">' + escHtml(r.role || '') +
        (r.crew_name ? '<div style="font-weight:400;font-size:8px;color:' + MUTE + ';">' + escHtml(r.crew_name) + '</div>' : '') +
        '</td>' + cells +
        '<td style="padding:5px 7px;text-align:center;font-weight:700;font-size:9px;">' + c.workHours + '</td>' +
        '<td style="padding:5px 7px;text-align:center;font-weight:700;font-size:9px;">' + c.restHours + '</td></tr>'
    }).join('')

    const covRow = res.coverage.map((c, h) => {
      const low = res.lowHours.indexOf(h) > -1
      return '<td style="padding:4px 2px;text-align:center;font-size:8.5px;font-weight:700;' +
        (low ? 'background:rgba(201,59,71,.18);color:#C93B47;' : 'color:' + MUTE + ';') + '">' + c + '</td>'
    }).join('')

    const verdict = res.compliant && res.lowHours.length === 0
      ? '<span style="color:#137050;font-weight:700;">MLC compliant &middot; coverage met</span>'
      : '<span style="color:#C93B47;font-weight:700;">Review required</span>'

    const issues = res.perRow.flatMap(r => r.check.issues.map(i =>
      '<div style="font-size:9px;padding:2px 0;color:' + (i.level === 'red' ? '#C93B47' : '#8a5c0a') + ';"><b>' + escHtml(r.role) + '</b> &mdash; ' + i.msg + '</div>')).join('')

    const html = '<div style="width:1080px;padding:6px 10px;font-family:Inter,Helvetica,Arial,sans-serif;color:' + TEXT + ';background:#fff;">' +
      '<div style="display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid ' + GOLD + ';padding-bottom:9px;">' +
        '<div><div style="display:flex;align-items:center;gap:11px;">' + wave +
          '<span style="font-size:20px;font-weight:800;letter-spacing:.13em;color:' + NAVY + ';">HEYCAPTAIN</span></div>' +
          '<div style="font-size:9px;color:' + MUTE + ';letter-spacing:.2em;text-transform:uppercase;margin-top:3px;margin-left:41px;">Watch Schedule</div></div>' +
        '<div style="text-align:right;font-size:9.5px;color:' + MUTE + ';line-height:1.7;">' +
          '<div style="font-size:14px;font-weight:700;color:' + TEXT + ';">' + escHtml(vessel) + '</div>' +
          '<div>' + fmtLong(dateFrom) + (dateTo && dateTo !== dateFrom ? ' &ndash; ' + fmtLong(dateTo) : '') + '</div></div>' +
      '</div>' +
      '<div style="background:' + NAVY + ';color:#fff;padding:6px 11px;font-size:10px;letter-spacing:.18em;text-transform:uppercase;margin:0 0 14px;">' +
        escHtml(tpl ? tpl.name : 'Custom roster') + ' &middot; ' + covDesc + (days > 1 ? ' &middot; ' + days + ' days' : '') + '</div>' +
      '<table style="width:100%;border-collapse:collapse;">' +
        '<thead><tr><th style="padding:5px 8px;text-align:left;font-size:8.5px;letter-spacing:.08em;text-transform:uppercase;color:#fff;background:' + NAVY + ';">Crew</th>' + hdr +
        '<th style="padding:5px 6px;font-size:8px;color:#fff;background:' + NAVY + ';">Work</th>' +
        '<th style="padding:5px 6px;font-size:8px;color:#fff;background:' + NAVY + ';">Rest</th></tr></thead>' +
        '<tbody>' + body +
        '<tr><td style="padding:5px 8px;font-size:8px;font-weight:700;color:' + MUTE + ';text-transform:uppercase;letter-spacing:.06em;">On deck</td>' + covRow + '<td colspan="2"></td></tr>' +
        '</tbody></table>' +
      '<div style="margin-top:12px;font-size:10px;">' + verdict + '</div>' +
      (issues ? '<div style="margin-top:6px;">' + issues + '</div>' : '') +
      '<div style="margin-top:16px;padding-top:9px;border-top:1px solid ' + LINE + ';display:flex;justify-content:space-between;font-size:8.5px;color:' + MUTE + ';">' +
        '<span style="font-weight:800;letter-spacing:.12em;color:' + NAVY + ';">HEYCAPTAIN</span>' +
        '<span>' + escHtml(vessel) + ' &middot; Watch Schedule &middot; generated ' + fmtLong(todayLocal()) + '</span></div>' +
    '</div>'

    const el = document.createElement('div')
    el.innerHTML = html
    document.body.appendChild(el)
    const fname = ('Watch_Schedule_' + vessel + '_' + dateFrom).replace(/[^a-z0-9]+/gi, '_') + '.pdf'
    const opt = { margin: 8, filename: fname, image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, windowWidth: 1080, width: 1080 },
      pagebreak: { mode: ['legacy'] }, jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' } }
    html2pdf().set(opt).from(el).save().then(() => document.body.removeChild(el))
  }

  function loadSaved(r) {
    let g = r.grid
    if (typeof g === 'string') { try { g = JSON.parse(g) } catch (e) { g = null } }
    if (!Array.isArray(g) || !g.length) { alert('That schedule has no crew rows saved.'); return }
    const clean = g.filter(x => x && typeof x.cells === 'string' && x.cells.length === 24)
    if (!clean.length) { alert('That schedule is corrupt - no valid 24-hour rows.'); return }
    setTplId(null)
    setRows(clean.map(x => ({ ...x })))
    setMinOnDeck(Array.isArray(r.min_on_deck_hours) && r.min_on_deck_hours.length === 24
      ? r.min_on_deck_hours
      : normaliseMin(r.min_on_deck ?? 0, r.coverage_window || null))
    setCovWin(r.coverage_window || null)
    setDateFrom(r.date_from || todayLocal())
    setDateTo(r.date_to || todayLocal())
    setShowArchive(false)
  }

  const cell = (v, bad) => ({
    width: 34, height: 26, textAlign: 'center', fontSize: 9.5, fontWeight: 600,
    cursor: 'pointer', userSelect: 'none', border: '1px solid #fff',
    background: v === 'W' ? (bad ? 'rgba(201,59,71,.22)' : 'rgba(23,138,92,.16)') : 'rgba(201,59,71,.10)',
    color: v === 'W' ? '#0f6a47' : '#a33', lineHeight: '24px',
  })

  useEffect(() => {
    const up = () => setPainting(null)
    window.addEventListener('mouseup', up)
    return () => window.removeEventListener('mouseup', up)
  }, [])

  return (
    <div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <input value={aiText} onChange={e => setAiText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') askAi() }}
          placeholder='Change the roster in words — e.g. "give the deckhand a later start" or "the bosun needs the afternoon off"'
          style={{ flex: 1, padding: '10px 13px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--card, #fff)', color: 'var(--text)', fontSize: 13 }} />
        <button onClick={askAi} disabled={aiBusy} style={{ padding: '10px 18px', borderRadius: 10, border: 'none', background: 'var(--gold, #C9A84C)', color: '#0A1628', fontWeight: 600, fontSize: 12.5, cursor: 'pointer' }}>
          {aiBusy ? 'Thinking\u2026' : 'Ask'}
        </button>
      </div>

      {aiProposal && (
        <div style={{ padding: '13px 16px', borderRadius: 11, marginBottom: 14,
          border: '1px solid ' + (aiProposal.after.compliant ? 'var(--border)' : 'rgba(201,59,71,.35)'),
          background: aiProposal.after.compliant ? 'rgba(201,168,76,.07)' : 'rgba(201,59,71,.06)' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 3 }}>Proposed change</div>
          {aiProposal.reasoning && <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>{aiProposal.reasoning}</div>}
          {aiProposal.changes.map((c, i) => (
            <div key={i} style={{ fontSize: 12, color: 'var(--text)', padding: '2px 0' }}>
              <b>{rows[c.row].crew_name || rows[c.row].role}</b>
              <span style={{ color: 'var(--muted)' }}> {'\u2014'} now {c.cells.split('').filter(x => x === 'W').length}h work</span>
            </div>
          ))}
          {!aiProposal.after.compliant && (
            <div style={{ fontSize: 12, color: '#C93B47', fontWeight: 600, marginTop: 7 }}>
              This would break MLC:
              {aiProposal.after.perRow.flatMap(r => r.check.issues.filter(i => i.level === 'red').map((iss, k) => (
                <div key={r.role + k} style={{ fontWeight: 400 }}>{r.role} {'\u2014'} {iss.msg}</div>
              )))}
            </div>
          )}
          {aiProposal.after.lowHours.length > 0 && (
            <div style={{ fontSize: 12, color: '#8a5c0a', marginTop: 5 }}>
              Coverage drops below the minimum at {aiProposal.after.lowHours.map(h => String(h).padStart(2, '0') + ':00').join(', ')}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 11 }}>
            <button onClick={() => { setRows(aiProposal.rows); setAiProposal(null); setAiText('') }}
              style={{ padding: '7px 15px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
                background: aiProposal.after.compliant ? 'var(--gold, #C9A84C)' : '#C93B47',
                color: aiProposal.after.compliant ? '#0A1628' : '#fff' }}>
              {aiProposal.after.compliant ? 'Apply' : 'Apply anyway'}
            </button>
            <button onClick={() => setAiProposal(null)} style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, cursor: 'pointer', border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)' }}>Discard</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        {SEED_TEMPLATES.map(t => (
          <button key={t.id} onClick={() => loadTemplate(t.id)} style={{
            padding: '6px 13px', borderRadius: 9, fontSize: 12, cursor: 'pointer',
            border: '1px solid ' + (tplId === t.id ? 'var(--gold, #C9A84C)' : 'var(--border)'),
            background: tplId === t.id ? 'rgba(201,168,76,.13)' : 'transparent',
            color: tplId === t.id ? 'var(--text)' : 'var(--muted)', fontWeight: tplId === t.id ? 600 : 400,
          }}>{t.name}</button>
        ))}
        <div style={{ flex: 1 }} />
        <label style={{ fontSize: 12, color: 'var(--muted)' }} title="Set the minimum for every covered hour - or edit hour by hour in the Required row">Set all
          <input type="number" min="0" max="12" defaultValue={Math.max(...minOnDeck)}
            onChange={e => { const n = Number(e.target.value) || 0; setMinOnDeck(prev => prev.map((v, h) => (covWin && (h < covWin[0] || h >= covWin[1])) ? v : n)) }}
            style={{ width: 46, marginLeft: 6, padding: '4px 6px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)' }} />
        </label>
        <button onClick={addCrew} style={{ padding: '6px 12px', borderRadius: 9, fontSize: 12, cursor: 'pointer', border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)' }}>+ Crew</button>
        {res.lowHours.length > 0 && (
          <button onClick={runRebalance} style={{ padding: '6px 13px', borderRadius: 9, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            border: '1px solid #1B4FD8', background: 'rgba(27,79,216,.10)', color: '#1B4FD8' }}>Rebalance</button>
        )}
        <button onClick={runCompare} disabled={comparing} title="Compare the plan with what was actually recorded"
          style={{ padding: '6px 12px', borderRadius: 9, fontSize: 12, cursor: 'pointer', border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)' }}>
          {comparing ? 'Comparing\u2026' : 'Plan vs actual'}
        </button>
        <button onClick={exportPdf} style={{ padding: '6px 12px', borderRadius: 9, fontSize: 12, cursor: 'pointer', border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)' }}>Export PDF</button>
        <button onClick={() => setShowArchive(true)} style={{ padding: '6px 12px', borderRadius: 9, fontSize: 12, cursor: 'pointer', border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)' }}>Archive</button>
        <button onClick={save} disabled={saving} style={{ padding: '6px 14px', borderRadius: 9, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: 'var(--gold, #C9A84C)', color: '#0A1628' }}>{saving ? 'Saving\u2026' : 'Save'}</button>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14, fontSize: 12, color: 'var(--muted)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>From
          <DateField value={dateFrom} onChange={v => setDateFrom(v || todayLocal())}
            style={{ padding: '5px 9px', borderRadius: 7, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', fontSize: 12, width: 130 }} />
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>To
          <DateField value={dateTo} onChange={v => setDateTo(v || todayLocal())}
            style={{ padding: '5px 9px', borderRadius: 7, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', fontSize: 12, width: 130 }} />
        </span>
        <span style={{ fontWeight: 600, color: 'var(--text)' }}>{days} day{days > 1 ? 's' : ''}</span>
        {days >= 7 && res.amberCount > 0 && (
          <span style={{ color: '#C93B47', fontWeight: 600 }}>
            Run for {days} days this breaches the 77h/7-day rule &mdash; the amber rows become violations.
          </span>
        )}
      </div>

      {proposal && (
        <div onClick={() => setProposal(null)} style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(10,22,40,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--card, #fff)', borderRadius: 14, width: 720, maxWidth: '96%', maxHeight: '82vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 70px rgba(0,0,0,.35)' }}>
            <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>Rebalance &mdash; pick an option</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>
                Hours-neutral: nobody works more, watches move. Nothing changes until you choose.
              </div>
            </div>
            <div style={{ overflowY: 'auto', padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {(proposal.options || []).map(o => (
                <div key={o.id} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text)' }}>{o.name}</div>
                    <div style={{ flex: 1 }} />
                    <button onClick={() => acceptOption(o)} style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: 'var(--gold, #C9A84C)', color: '#0A1628' }}>Use this</button>
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--muted)', margin: '4px 0 8px' }}>{o.note}</div>
                  {o.changes.map((c, i) => (
                    <div key={i} style={{ fontSize: 12.5, color: 'var(--text)', padding: '3px 0' }}>
                      <b>{c.who}</b> &mdash; <span style={{ color: '#137050', fontWeight: 600 }}>+watch {String(c.addHour).padStart(2, '0')}:00</span>
                      <span style={{ color: 'var(--muted)' }}> &rarr; </span>
                      <span style={{ color: '#8a5c0a', fontWeight: 600 }}>rest {String(c.dropHour).padStart(2, '0')}:00</span>
                    </div>
                  ))}
                  <div style={{ marginTop: 7, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {o.affected.map((a, i) => (
                      <span key={i} style={{ fontSize: 11, color: a.margin === 0 ? '#8a5c0a' : 'var(--muted)', fontWeight: a.margin === 0 ? 600 : 400 }}>
                        {a.who}: {a.restHours}h rest{a.margin === 0 ? ' \u00b7 zero weekly margin' : ''}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
              {proposal.note && <div style={{ fontSize: 12.5, color: '#C93B47', fontWeight: 600, padding: '6px 0' }}>{proposal.note}</div>}
              {(proposal.failed || []).map((f, i) => (
                <div key={i} style={{ fontSize: 11.5, color: 'var(--muted)' }}>Not possible &mdash; {f.strategy}: {f.reason}</div>
              ))}
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setProposal(null)} style={{ padding: '8px 14px', borderRadius: 9, fontSize: 12, cursor: 'pointer', border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {compare && (
        <div onClick={() => setCompare(null)} style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(10,22,40,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--card, #fff)', borderRadius: 14, width: 720, maxWidth: '96%', maxHeight: '82vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 70px rgba(0,0,0,.35)' }}>
            <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>Plan vs actual</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>
                What the roster planned, against what crew actually recorded in Hours of Rest. Read-only.
              </div>
            </div>
            <div style={{ overflowY: 'auto', padding: '12px 20px' }}>
              {compare.result.map((r, i) => (
                <div key={i} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <b style={{ fontSize: 13.5, color: 'var(--text)' }}>{r.who}</b>
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>{r.role}</span>
                    <div style={{ flex: 1 }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: r.totalDelta > 0 ? '#C93B47' : r.totalDelta < 0 ? '#137050' : 'var(--muted)' }}>
                      {r.withData === 0 ? 'no record' : (r.totalDelta > 0 ? '+' + r.totalDelta + 'h worked over plan' : r.totalDelta < 0 ? r.totalDelta + 'h under plan' : 'as planned')}
                    </span>
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 4 }}>
                    Planned {r.plannedWork}h/day &middot; {r.withData} of {r.perDay.length} days recorded
                    {r.missing > 0 && <span style={{ color: '#8a5c0a' }}> &middot; {r.missing} day{r.missing > 1 ? 's' : ''} missing</span>}
                  </div>
                  {r.perDay.filter(d => d.recorded && d.diffHours.length > 0).map((d, j) => (
                    <div key={j} style={{ fontSize: 11.5, color: 'var(--text)', marginTop: 3 }}>
                      {d.date}: worked {d.actualWork}h ({d.delta > 0 ? '+' + d.delta : d.delta}h) &mdash; differs at {d.diffHours.map(h => String(h).padStart(2, '0') + ':00').join(', ')}
                    </div>
                  ))}
                  {r.identical && (
                    <div style={{ fontSize: 11.5, color: '#8a5c0a', marginTop: 4, fontWeight: 600 }}>
                      Recorded hours match the plan exactly on every day. Worth checking these are real.
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setCompare(null)} style={{ padding: '8px 14px', borderRadius: 9, fontSize: 12, cursor: 'pointer', border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showArchive && (
        <div onClick={() => setShowArchive(false)} style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(10,22,40,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--card, #fff)', borderRadius: 14, width: 560, maxWidth: '94%', maxHeight: '76vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 70px rgba(0,0,0,.35)' }}>
            <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--border)', fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>Saved schedules</div>
            <div style={{ overflowY: 'auto', padding: '4px 20px 12px' }}>
              {archive.map(r => (
                <div key={r.id} onClick={() => loadSaved(r)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{r.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                      {r.date_from ? r.date_from + (r.date_to && r.date_to !== r.date_from ? ' → ' + r.date_to : '') : ''} · {(r.grid || []).length} crew
                    </div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); loadSaved(r) }}
                    style={{ fontSize: 11, color: 'var(--gold, #C9A84C)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px' }}>Load</button>
                  <button onClick={async (e) => { e.stopPropagation(); if (confirm('Delete the schedule "' + (r.name || 'schedule') + '"? You can undo it for 10 seconds.')) { try { await deleteWithUndo(supabase, { table: 'watch_schedules', id: r.id, label: 'schedule "' + (r.name || 'schedule') + '"', onUndone: () => loadArchive() }) } catch (err) { alert('Could not delete: ' + (err.message || err)) } loadArchive() } }}
                    style={{ fontSize: 13, color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px' }}>&times;</button>
                </div>
              ))}
              {!archive.length && <div style={{ padding: 20, fontSize: 12, color: 'var(--muted)' }}>Nothing saved yet.</div>}
            </div>
          </div>
        </div>
      )}

      {feas.feasible && feas.tight && (
        <div style={{ padding: '9px 13px', marginBottom: 12, borderRadius: 9, fontSize: 12.5,
          background: 'rgba(185,125,20,.08)', border: '1px solid rgba(185,125,20,.3)', color: '#8a5c0a' }}>
          This coverage needs {feas.demand} man-hours a day and {rows.length} crew can legally supply {feas.supply}. It fits, but with {feas.slack}h to spare &mdash; one sick day and it breaks.
        </div>
      )}

      {!feas.feasible && (
        <div style={{ padding: '10px 14px', marginBottom: 14, borderRadius: 10, fontSize: 12.5,
          background: 'rgba(201,59,71,.09)', border: '1px solid rgba(201,59,71,.35)', color: '#C93B47', fontWeight: 600 }}>
          Not achievable: {feas.msg}
        </div>
      )}

      {tpl?.captainVariants && (
        <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 10, background: 'rgba(201,168,76,.08)', border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>Captain — pick a pattern</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {tpl.captainVariants.map((v, i) => (
              <button key={i} onClick={() => applyVariant(v.cells)} title={v.note} style={{
                padding: '6px 12px', borderRadius: 8, fontSize: 11.5, cursor: 'pointer', textAlign: 'left',
                border: '1px solid var(--border)', background: 'var(--card, #fff)', color: 'var(--text)', maxWidth: 260,
              }}>
                <div style={{ fontWeight: 600 }}>{v.name}</div>
                <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 2 }}>{v.note}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* iPad (600-1279px): the 24 hours share the width (fixed layout, W / R in the cell) - no sideways scroll (ipad) */}
      <style>{`
        @media (min-width: 600px) and (max-width: 1279px) {
          /* a real table again (the phone shell turns tables into scrolling blocks), so the fixed layout applies */
          .hc-shell .hc-content .hc-ws-grid table { display: table !important; overflow: visible !important; width: 100% !important; table-layout: fixed; }
          .hc-ws-grid th, .hc-ws-grid td { min-width: 0 !important; }
          .hc-ws-grid thead th:first-child { width: 104px; padding: 7px 6px !important; }
          .hc-ws-grid thead th:nth-last-child(2), .hc-ws-grid thead th:nth-last-child(3) { width: 34px; padding: 7px 2px !important; }
          .hc-ws-grid thead th:last-child { width: 20px; padding: 0 !important; }
          .hc-ws-grid tbody td:first-child { padding: 4px 6px !important; white-space: normal !important; }
          .hc-ws-grid tbody td:first-child input, .hc-ws-grid tbody td:first-child select { width: 100% !important; box-sizing: border-box; }
          .hc-ws-grid tbody td { padding-left: 1px !important; padding-right: 1px !important; }
          .hc-ws-grid .hc-ws-cell { width: auto !important; }
          .hc-ws-grid .hc-ws-long { display: none; }
          .hc-ws-grid .hc-ws-short { display: inline !important; }
        }
      `}</style>
      <div className="hc-ws-grid" style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr>
              <th style={{ padding: '7px 10px', textAlign: 'left', fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', color: '#fff', background: '#0A1628' }}>Crew</th>
              {HOURS.map((h, i) => (
                <th key={h} style={{ padding: '7px 2px', fontSize: 9.5, color: '#fff', background: '#0A1628', minWidth: 34,
                  opacity: covWin && (i < covWin[0] || i >= covWin[1]) ? 0.45 : 1 }}>{h.slice(0, 2)}</th>
              ))}
              <th style={{ padding: '7px 8px', fontSize: 9.5, color: '#fff', background: '#0A1628' }}>Work</th>
              <th style={{ padding: '7px 8px', fontSize: 9.5, color: '#fff', background: '#0A1628' }}>Rest</th>
              <th style={{ padding: '7px 8px', fontSize: 9.5, color: '#fff', background: '#0A1628' }}></th>
            </tr>
          </thead>
          <tbody>
            {res.perRow.map((r, ri) => {
              const red = r.check.issues.some(i => i.level === 'red')
              const amber = !red && r.check.issues.some(i => i.level === 'amber')
              return (
                <tr key={ri}>
                  <td style={{ padding: '4px 10px', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap',
                    borderLeft: '3px solid ' + (red ? '#C93B47' : amber ? '#8a5c0a' : 'transparent') }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <input value={r.role} onChange={e => setRows(rs => rs.map((x, i) => i === ri ? { ...x, role: e.target.value } : x))}
                        style={{ width: 118, border: 'none', background: 'transparent', font: 'inherit', fontWeight: 700, fontSize: 11, color: 'var(--text)' }} />
                      <select value={r.crew_id || ''} onChange={e => {
                          const c = crew.find(x => String(x.id) === e.target.value)
                          setRows(rs => rs.map((x, i) => i === ri ? { ...x, crew_id: c ? c.id : null, crew_name: c ? c.name : null } : x))
                        }}
                        style={{ width: 118, fontSize: 10, padding: '2px 3px', borderRadius: 5, border: '1px solid var(--border)', background: 'transparent', color: r.crew_name ? 'var(--text)' : 'var(--muted)' }}>
                        <option value="">Unassigned</option>
                        {crew.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                  </td>
                  {r.cells.split('').map((v, h) => (
                    <td key={h} style={{ padding: 0 }}>
                      <div
                        onMouseDown={() => { const nv = v === 'W' ? 'R' : 'W'; setPainting(nv); setCell(ri, h, nv) }}
                        onMouseEnter={(e) => { if (painting && e.buttons === 1) setCell(ri, h, painting) }}
                        className="hc-ws-cell" style={cell(v, red)}><span className="hc-ws-long">{v === 'W' ? 'Work' : 'Rest'}</span><span className="hc-ws-short" style={{ display: 'none' }}>{v === 'W' ? 'W' : 'R'}</span></div>
                    </td>
                  ))}
                  <td style={{ padding: '4px 8px', textAlign: 'center', fontWeight: 700, color: red ? '#C93B47' : 'var(--text)' }}>{r.check.workHours}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'center', fontWeight: 700, color: red ? '#C93B47' : 'var(--text)' }}>{r.check.restHours}</td>
                  <td style={{ padding: '4px 6px' }}>
                    <button onClick={() => removeCrew(ri)} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 13 }}>&times;</button>
                  </td>
                </tr>
              )
            })}
            <tr>
              <td style={{ padding: '7px 10px', fontSize: 10, fontWeight: 700, color: 'var(--muted)', letterSpacing: '.06em', textTransform: 'uppercase' }}
                title="Click to raise, right-click to lower">Required</td>
              {minOnDeck.map((req, h) => (
                <td key={h} style={{ padding: 0 }}>
                  <div
                    onClick={() => setMinOnDeck(prev => prev.map((v, i) => i === h ? Math.min(12, v + 1) : v))}
                    onContextMenu={(e) => { e.preventDefault(); setMinOnDeck(prev => prev.map((v, i) => i === h ? Math.max(0, v - 1) : v)) }}
                    style={{ height: 22, lineHeight: '22px', textAlign: 'center', fontSize: 10.5, fontWeight: 600, cursor: 'pointer',
                      color: req > 0 ? 'var(--text)' : 'var(--faint, #98A3B8)',
                      background: req > 0 ? 'rgba(201,168,76,.10)' : 'transparent' }}>{req || '\u00b7'}</div>
                </td>
              ))}
              <td colSpan={3}></td>
            </tr>
            <tr>
              <td style={{ padding: '7px 10px', fontSize: 10, fontWeight: 700, color: 'var(--muted)', letterSpacing: '.06em', textTransform: 'uppercase' }}>On deck</td>
              {res.coverage.map((c, h) => {
                const low = res.lowHours.includes(h)
                return (
                  <td key={h} style={{ padding: 0 }}>
                    <div style={{ height: 24, lineHeight: '24px', textAlign: 'center', fontSize: 11, fontWeight: 700,
                      background: low ? 'rgba(201,59,71,.18)' : 'transparent',
                      color: low ? '#C93B47' : 'var(--muted)' }}>{c}</div>
                  </td>
                )
              })}
              <td colSpan={3}></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 14 }}>
        {res.perRow.map((r, i) => r.check.issues.map((iss, j) => (
          <div key={i + '-' + j} style={{ fontSize: 12, padding: '5px 0', color: iss.level === 'red' ? '#C93B47' : '#8a5c0a' }}>
            <b>{r.role}</b> &mdash; {iss.msg}
          </div>
        )))}
        {res.lowHours.length > 0 && (
          <div style={{ fontSize: 12, padding: '5px 0', color: '#8a5c0a' }}>
            <b>Coverage</b> &mdash; below {minOnDeck} on deck at {res.lowHours.map(h => String(h).padStart(2, '0') + ':00').join(', ')}
          </div>
        )}
        {res.compliant && res.lowHours.length === 0 && res.amberCount === 0 && (
          <div style={{ fontSize: 12.5, padding: '6px 0', color: '#137050', fontWeight: 600 }}>MLC compliant &middot; coverage met</div>
        )}
      </div>
    </div>
  )
}
